import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const DoggyDayCareFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
  isFacilitiesEnabled = false,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersCanProvideDailyExercisePct,
    sittersCanProvideMedicationPct,
    sittersCanProvideSpecialCarePct,
    sittersWithMinResponseTimeIn60minPct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="doggy-day-care-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Doggy Day Care FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {isFacilitiesEnabled && (
          <FrequentlyAskedQuestion
            question={t`What’s the difference between in-home doggy daycare and facility-based doggy daycare?`}
          >
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Select
                value={localityType}
                _city={
                  <Trans>
                    Only Rover provides comprehensive {cityName} doggy day care options that meet
                    your dog's unique needs. Anxious or special needs dogs who prefer a 1:1
                    environment are a good fit for in-home doggy day care, while energetic pups who
                    benefit from socialization and activity are a good fit for doggy day care
                    centers.
                  </Trans>
                }
                _neighborhood={
                  <Trans>
                    Only Rover provides comprehensive {neighborhoodName} doggy day care options that
                    meet your dog's unique needs. Anxious or special needs dogs who prefer a 1:1
                    environment are a good fit for in-home doggy day care, while energetic pups who
                    benefit from socialization and activity are a good fit for doggy day care
                    centers.
                  </Trans>
                }
                other={
                  <Trans>
                    Only Rover provides comprehensive {locationName} doggy day care options that
                    meet your dog's unique needs. Anxious or special needs dogs who prefer a 1:1
                    environment are a good fit for in-home doggy day care, while energetic pups who
                    benefit from socialization and activity are a good fit for doggy day care
                    centers.
                  </Trans>
                }
              />
            </Paragraph>
          </FrequentlyAskedQuestion>
        )}
        {/* 2 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a sitter for doggy day care in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a sitter for doggy day care in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} sitter for doggy day care?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        sitter for doggy day care in {cityName} on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per day, after factoring
                        in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your dog’s needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        sitter for doggy day care in {neighborhoodName} on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per day, after factoring
                        in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your dog’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Sitters on Rover are free to set their rates. The median cost to hire a
                        {locationName} sitter for doggy day care on Rover as of{' '}
                        {yearMonthLastUpdated} was about {formattedAmount} per day, after factoring
                        in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A sitter’s rate may also change as you customize your booking to fit your
                        and your dog’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 3 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I contact a ${locationName} sitter for doggy day care?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} doggy day care sitter for the first
                    time, visit the sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a sitter before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 4 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: sittersCount } = maxSearchableSittersNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many sitters provide doggy day care in ${cityName}?`,
                  neighborhood: `How many sitters provide doggy day care in ${neighborhoodName}?`,
                  other: `How many ${locationName} sitters provide doggy day care?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided doggy day care
                        in {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, sitters
                        offering doggy day care joining Rover must pass a background check for you
                        and your dog’s safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} sitters provided doggy day care
                        in {neighborhoodName}. You can filter, sort, expand your radius, read
                        reviews, and compare pricing to find the perfect sitter near you. As a
                        reminder, sitters offering doggy day care joining Rover must pass a
                        background check for you and your dog’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, {sittersCount} {locationName} sitters provided
                        doggy day care. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect sitter near you. As a reminder, sitters
                        offering doggy day care joining Rover must pass a background check for you
                        and your dog’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a doggy day care sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a doggy day care sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} doggy day care sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple sitters about your booking.
                    Typically, {percent} of {locationName} sitters offering doggy day care respond
                    in under an hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Typically, sitters offering doggy day care on Rover respond in under an hour, so you
                should be matched with the perfect sitter for you and your dog in no time!
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are sitters providing doggy day care in ${cityName}?`,
            neighborhood: `How experienced are sitters providing doggy day care in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} sitters providing doggy day care?`,
          })}
        >
          {(() => {
            if (yearsOfExperienceNum && avgRepeatCustomerAmongSittersWithRepeatNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Sitters who provide doggy day care in {locationName} have an average of{' '}
                        {yearsOfExperience} years of experience. Sitters offering doggy day care
                        with repeat customers have an average of {repeatCustomersCount} repeat
                        clients. Experience can vary from sitter to sitter, but you can view
                        reviews, years of experience, and number of repeat clients when you compare
                        sitters in {locationName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters who provide doggy day care in {locationName} have an average of{' '}
                        {yearsOfExperience} years of experience. Sitters offering doggy day care
                        with repeat customers have an average of {repeatCustomersCount} repeat
                        clients. Experience can vary from sitter to sitter, but you can view
                        reviews, years of experience, and number of repeat clients when you compare
                        sitters in {locationName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} sitters who provide doggy day care have an average of{' '}
                        {yearsOfExperience} years of experience. Sitters offering doggy day care
                        with repeat customers have an average of {repeatCustomersCount} repeat
                        clients. Experience can vary from sitter to sitter, but you can view
                        reviews, years of experience, and number of repeat clients when you compare
                        sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (avgRepeatCustomerAmongSittersWithRepeatNum) {
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Sitters offering doggy day care with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when you compare sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters offering doggy day care with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when you compare sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Sitters offering doggy day care with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when you compare sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (yearsOfExperienceNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Sitters providing doggy day care in {cityName} have an average of{' '}
                        {yearsOfExperience} years of experience. This can vary widely from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when comparing sitters available in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Sitters providing doggy day care in {neighborhoodName} have an average of{' '}
                        {yearsOfExperience} years of experience. This can vary widely from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when comparing sitters available in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} sitters providing doggy day care have an average of{' '}
                        {yearsOfExperience} years of experience. This can vary widely from sitter to
                        sitter, but you can view reviews, years of experience, and number of repeat
                        clients when comparing sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            return (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters
                      available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from sitter to sitter, but you can view reviews,
                      years of experience, and number of repeat clients when comparing sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            );
          })()}
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of doggy day care services do sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of doggy day care services do sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of doggy day care services do ${locationName} sitters offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                {locationName} doggy day care sitters are happy to take care of your dog while
                you’re at work or unavailable for the day. Book a one-time visit, or set up a
                regular rhythm with your favorite {locationName} sitter. Drop off your dog at the
                sitter’s home and rest easy knowing they’ll get frequent potty breaks, plenty of
                playtime, and lots of love. Doggy day care is great for:
              </Trans>
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Puppies and high-energy dogs
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs with special needs, including seniors
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pet parents who work long hours
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs with separation anxiety
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        {maxSearchableSittersNum && (
          <FrequentlyAskedQuestion
            question={t`My dog has special needs. Can I find a doggy day care sitter on Rover who can accommodate my needs?`}
          >
            {sittersCanProvideSpecialCarePct &&
            sittersCanProvideDailyExercisePct &&
            sittersCanProvideMedicationPct
              ? (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  const dailyExercisePercent = sittersCanProvideDailyExercisePct.asPercent;
                  const provideMedicationPercent = sittersCanProvideMedicationPct.asPercent;
                  const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} sitters offering doggy day care in {cityName} on
                            Rover, so you’re sure to find the perfect match for your dog! In{' '}
                            {cityName}, {dailyExercisePercent} of sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect doggy day care sitter near you.
                            As a reminder, sitters joining Rover must pass a background check for
                            you and your dog’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} sitters offering doggy day care in{' '}
                            {neighborhoodName} on Rover, so you’re sure to find the perfect match
                            for your dog! In {neighborhoodName}, {dailyExercisePercent} of sitters
                            offer daily exercise, {provideMedicationPercent} offer medication
                            administration, and {specialCarePercent} have experience with senior
                            dogs and dogs with special needs. We know each dog is unique and may
                            have specific care instructions. You can filter, sort, expand your
                            radius, read reviews, and compare pricing to find the perfect doggy day
                            care sitter near you. As a reminder, sitters joining Rover must pass a
                            background check for you and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} sitters offering doggy day care
                            on Rover, so you’re sure to find the perfect match for your dog!{' '}
                            {dailyExercisePercent} of {locationName} sitters offer daily exercise,{' '}
                            {provideMedicationPercent} offer medication administration, and{' '}
                            {specialCarePercent} have experience with senior dogs and dogs with
                            special needs. We know each dog is unique and may have specific care
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect doggy day care sitter near you.
                            As a reminder, sitters joining Rover must pass a background check for
                            you and your dog’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()
              : (() => {
                  const sitterCount = maxSearchableSittersNum.asNumber;
                  return (
                    <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                      <Select
                        value={localityType}
                        _city={
                          <Trans>
                            There are {sitterCount} sitters offering doggy day care in {cityName} on
                            Rover, so you’re sure to find the perfect match for your dog! We
                            understand that each dog is unique and may require specific
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect doggy day care sitter near you.
                            As a reminder, sitters joining Rover must pass a background check for
                            you and your dog’s safety.
                          </Trans>
                        }
                        _neighborhood={
                          <Trans>
                            There are {sitterCount} sitters offering doggy day care in{' '}
                            {neighborhoodName} on Rover, so you’re sure to find the perfect match
                            for your dog! We understand that each dog is unique and may require
                            specific instructions. You can filter, sort, expand your radius, read
                            reviews, and compare pricing to find the perfect doggy day care sitter
                            near you. As a reminder, sitters joining Rover must pass a background
                            check for you and your dog’s safety.
                          </Trans>
                        }
                        other={
                          <Trans>
                            There are {sitterCount} {locationName} sitters offering doggy day care
                            on Rover, so you’re sure to find the perfect match for your dog! We
                            understand that each dog is unique and may require specific
                            instructions. You can filter, sort, expand your radius, read reviews,
                            and compare pricing to find the perfect doggy day care sitter near you.
                            As a reminder, sitters joining Rover must pass a background check for
                            you and your dog’s safety.
                          </Trans>
                        }
                      />
                    </Paragraph>
                  );
                })()}
          </FrequentlyAskedQuestion>
        )}

        {/* 9 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do sitters providing doggy day care in ${cityName} pass background checks?`,
            neighborhood: `Do sitters providing doggy day care in ${neighborhoodName} pass background checks?`,
            other: `Do ${locationName} sitters providing doggy day care pass background checks?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your doggy day care sitter through
              messages on Rover for delightful photo updates. The Rover team is available 24/7, and
              your sitter has access to advice from qualified veterinary professionals. In the rare
              event of any issues during a booking, rest easy knowing your pet is covered by the
              Rover Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 10 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I cancel a doggy day care service and receive a refund?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your doggy day care sitter requested to cancel this
                      booking, then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your doggy day care
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default DoggyDayCareFaqs;
